import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { golf, eids } from './enums';
import golfToEid from 'golf-to-eid';
import Link from '@ux/link';
import Button from '@ux/button';
import rip from 'rip-out';
import SubNavPanel from './subnav-panel';
import { IntlProvider, FormattedMessage } from 'react-intl';
import ChevDown from '@ux/icon/chevron-down';
import ChevRight from '@ux/icon/chevron-right';
import ChevLeft from '@ux/icon/chevron-left';
import Home from '@ux/icon/home';
import User from '@ux/icon/user';
import Close from '@ux/icon/x';
import Help from '@ux/icon/help';
import Hamburger from '@ux/icon/hamburger';
import Phone from '@ux/icon/phone';
import classnames from 'classnames';

export class MobileNav extends Component {
  constructor() {
    super(...arguments);

    this.state = {
      selectedItem: null,
      displaySubNavPanel: false,
      open: false,
      runway: false,
      hideOnScroll: false
    };

    this.html = {};
    this.close = this.close.bind(this);
    this.goToMainMenu = this.goToMainMenu.bind(this);
    this.toggleOpen = this.toggleOpen.bind(this);
    this.killRunway = this.killRunway.bind(this);
  }

  componentDidUpdate() {
    const { state } = this;
    let subMenuHeight = '';
    if (this.subNavPanel && this.subNavPanel.element) {
      subMenuHeight = this.subNavPanel.element.clientHeight;
    }
    const fauxScreenHeight = screen.height - 60;

    if (
      subMenuHeight &&
      subMenuHeight > fauxScreenHeight &&
      !state.runway &&
      state.displaySubNavPanel &&
      !state.hideOnScroll
    ) {
      this.setState({
        runway: true
      });
    } else if (!state.displaySubNavPanel && state.runway) {
      this.setState({
        runway: false
      });
    }
  }

  /**
   * close the menu by setting state.open to be false
   *
   * @private
   */
  close() {
    this.props.onClick(false);
    this.setState({
      open: false,
      runway: false,
      displaySubNavPanel: false,
      selectedItem: null
    });
    const html = this.html;
    html.className =
      classnames({
        ['mobile-menu-open']: false
      }) || '';
  }

  /**
   * get rid of the runway
   *
   * @private
   */
  killRunway() {
    if (!this.state.runway) {
      return;
    }
    this.setState({
      runway: false,
      hideOnScroll: true
    });
  }

  /**
   * click function to set open/close state of menu
   *
   * @private
   */
  toggleOpen() {
    const { open } = this.state;
    this.props.onClick(!open);
    this.setState({
      open: !open
    });
  }

  /**
   * click function to set which nav item is selected
   * as well as whether to display the main headings or the subnav
   *
   * @param {Object} item The item that was just clicked on
   * @param {Object} event The click event
   * @private
   */
  onHeadingClick(item, event) {
    if (item.onClick) {
      item.onClick(event);
    }

    this.setState({
      selectedItem: item,
      displaySubNavPanel: true
    });
  }

  /**
   * handles clicking on navigation items with an onClick handler
   *
   * @param {Object} item The item that was just clicked on
   * @param {Object} event The click event
   * @private
   */
  onItemClick(item, event) {
    item.onClick(event);
    this.close();
  }

  /**
   * click function to go back to the main menu
   *
   * @private
   */
  goToMainMenu() {
    this.setState({
      displaySubNavPanel: false,
      hideOnScroll: false
    });
  }

  /**
   * Render a uxcore2 icon
   * @param {string} icon The classname for a uxcore2 icon
   * @returns {React.Element} A span with an icon
   * @private
   */
  renderIcon(icon) {
    if (icon) {
      return (
        <span className={ classnames(icon, 'uxicon', 'custom-icon') } />
      );
    }
    return null;
  }

  /**
   * Render a heading that is a plain button with no children.
   * @param  {Object} item A navigation item that contains link data
   * @param {Number} idx The index at which the item appears in the nav array
   * @param  {String} eid Eid for click tracking
   * @param  {Boolean} active If the parent is active
   * @returns {ReactElement} Div containing a parent link
   */
  renderPlainButton(item, idx, eid, active) {
    const { urlArgs, useGradientHighlightBar } = this.props;
    const { caption, title, icon, id, href, dataAttrs, target, onClick } = item;

    const plainButton = (
      <Link
        key={ (caption || id) + idx }
        className={ classnames('column-heading', {
          active
        }) }
        href={ href }
        urlArgs={ urlArgs }
        data-eid={ eid }
        target={ target }
        onClick={ onClick && this.onItemClick.bind(this, item) }
        onClicked={ this.close }
        { ...dataAttrs }>
        { title || caption }
        { this.renderIcon(icon) }
      </Link>
    );

    if (!useGradientHighlightBar) {
      return plainButton;
    }

    return (
      <div className='column-container'>
        {active && (
          <div className='column-highlight' role='presentation'>
            { ' ' }
          </div>
        )}
        { plainButton }
      </div>
    );
  }

  /**
   * Render a heading button that has children
   * @param  {Object} item A parent navigation item that has children
   * @param {Number} idx The index at which the item appears in the nav array
   * @param  {String} eid Eid used for click tracking
   * @param  {Boolean} active If the parent is active
   * @returns {ReactElement} Div containing a parent that has children
   */
  renderHeadingWithChildren(item, idx, eid, active) {
    const { caption, title, id } = item;

    const menuItem = (
      <div
        key={ (caption || id) + idx }
        className={ classnames('column-heading', {
          active
        }) }
        onClick={ this.onHeadingClick.bind(this, item) }
        data-eid={ eid }>
        { title || caption }
        { this.renderIcon(item.icon) }
        <span className='menu-arrow-left pull-right'>
          <ChevRight width={ 16 } height={ 16 } />
        </span>
      </div>
    );

    if (!this.props.useGradientHighlightBar) {
      return menuItem;
    }

    return (
      <div className='column-container'>
        { active && (
          <div className='column-highlight' role='presentation'>
            { ' ' }
          </div>
        ) }
        { menuItem }
      </div>
    );
  }

  /**
   * render top level headings for nav
   *
   * @private
   * @returns {ReactElement} a set of divs
   */
  renderMobileHeadings() {
    const props = this.props;

    // eslint-disable-next-line complexity
    return props.nav
      .map((item, idx) => {
        if (item.desktopOnly) {
          return null;
        }

        const { caption, title, id, children, href } = item;

        if (caption || title) {
          const active =
            props.activeHeading &&
            ((props.activeHeading.caption &&
              ~props.activeHeading.caption.indexOf(caption)) ||
              (props.activeHeading.id && ~props.activeHeading.id.indexOf(id)));

          const eid =
            item.eid ||
            golfToEid(caption || id || '', {
              prefix: props.eidPrefix,
              postfix: 'link.click'
            });

          // add a divider above partner programs
          if (props.growthnav2 && id === 'Sales:ProductNav:PartnerPrograms') {
            let content;
            if (children && children.length > 0) {
              content = this.renderHeadingWithChildren(item, idx, eid, active);
            } else if (href) {
              content = this.renderPlainButton(item, idx, eid, active);
            }
            return (
              <React.Fragment key={ id + idx }>
                <hr />
                { content }
              </React.Fragment>
            );
          }

          if (children && children.length > 0) {
            return this.renderHeadingWithChildren(item, idx, eid, active);
          } else if (href) {
            return this.renderPlainButton(item, idx, eid, active);
          }
        }
      })
      .filter(menuItem => menuItem !== null);
  }

  /**
   * render gray header with close button
   * and conditionally a back button
   *
   * @param {Boolean} showBackBtn Whether or not to show the back button
   * @private
   * @returns {ReactElement} a div for the top header
   */
  renderTopContainer(showBackBtn) {
    const { props } = this;
    return (
      <div className='mobile-top-container'>
        { showBackBtn && (
          <Button
            onClick={ this.goToMainMenu }
            className='main-menu-btn'
            text={ this.props.menuTitle }
            icon={ <ChevLeft width={ 12 } height={ 12 } /> }
          >
          </Button>
        )}
        {!showBackBtn && props.showMenuTitleOnHome && (
          <div className='mobile-top-container-label font-primary-bold'>
            { this.props.menuTitle }
          </div>
        )}
        <span onClick={ this.close } className='close-button pull-right'>
          <Close width={ 24 } height={ 24 } />
        </span>
      </div>
    );
  }

  /**
   * Render array of all panels containing subnavs
   *
   * @private
   *
   * @returns {Array} div of SubNavPanels
   */
  renderSubNavPanels() {
    const { state, props } = this;
    const panels = [];
    const { selectedItem } = state;
    const { caption, title } = selectedItem || {};
    props.nav.forEach((navItem, i) => {
      if (navItem.desktopOnly) {
        return;
      }

      if (navItem.children) {
        const open =
          state.displaySubNavPanel &&
          ((caption && caption === navItem.caption) ||
            (title && title === navItem.title));
        panels.push(
          <SubNavPanel
            onClose={ this.close }
            onBack={ this.goToMainMenu }
            open={ open }
            navigation={ navItem }
            menuTitle={ props.menuTitle }
            eidPrefix={ props.eidPrefix }
            currentHref={ props.currentHref }
            urlArgs={ props.urlArgs }
            key={ 'subNavPanel' + i }
            ref={ r => {
              if (open) {
                this.subNavPanel = r;
              }
            } }
          />
        );
      }
    });

    return panels;
  }

  /**
   * Render panel displaying nav headings if navigation menu is open
   *
   * @private
   *
   * @returns {ReactElement} div for the nav headings
   */
  renderHeadingsPanel() {
    const { state, props } = this;
    if (typeof document !== 'undefined') {
      const userAgent = window.navigator.userAgent;
      const iOS = userAgent.match(/iPhone|iPad/i); // iPad and iPhone user agent for Safari
      const mobileRegExp = /(iPad|iPhone).*WebKit(?:(?!(CriOS|FxiOS|OPiOS)).)*$/;
      const mobileSafariClass = mobileRegExp.test(userAgent)
        ? `mobile-safari-${iOS[0].toLowerCase()}`
        : '';

      const html = (this.html = this.target || document.documentElement);

      // dedupe the 'mobile-menu-open'
      let openMenuClass = '';
      if (state.open && !html.className.includes('mobile-menu-open')) {
        openMenuClass = { ['mobile-menu-open']: true };
      }

      html.className = classnames(html.className, mobileSafariClass, openMenuClass);
    }

    return (
      <div
        className={ classnames('headings-panel', {
          open: !state.displaySubNavPanel && state.open
        }) }>
        { this.renderTopContainer() }
        <div className='mobile-wrap'>
          <div className='mobile-label font-primary-bold'>
            { props.menuTitle }
          </div>
          { this.renderMobileHeadings() }
        </div>
      </div>
    );
  }

  /**
   * Render panel displaying mobile nav buttons if navigation menu is open
   *
   * @private
   *
   * @returns {ReactElement} list of buttons
   */
  renderButtons() {
    const { state } = this;
    let props = this.props;
    props = rip(props, 'ariaLabel');

    let leftIcon = (
      <span aria-hidden='true'>
        <Phone width={ 24 } height={ 24 } />
      </span>
    );
    let leftHref = props.urls.contactUs && props.urls.contactUs.href;
    let leftA11y = props.messages[golf.callUs];
    let leftEid = eids.callUs;

    if (props.showHomeLink) {
      leftIcon = (
        <span aria-hidden='true'>
          <Home width={ 24 } height={ 24 } />
        </span>
      );
      if (props.privateLabelId === 1) {
        if (props.isInGoCohort) {
          leftHref = props.urls.accountHome && props.urls.accountHome.href;
          leftEid = 'uxp.hyd.mobile_nav.account_home.link.click';
        } else {
          leftHref = props.urls.products && props.urls.products.href;
          leftEid = 'uxp.hyd.mobile_nav.my_products.link.click';
        }
      } else {
        leftHref = props.urls.home && props.urls.home.href;
        leftEid = 'uxp.hyd.mobile_nav.home.link.click';
      }
      leftA11y = '';
    }

    return (
      <ul
        className={ classnames('mobile-buttons', 'list-unstyled', {
          closed: !state.open || state.displaySubNavPanel
        }) }>
        <li>
          <Link ariaLabel={ leftA11y } href={ leftHref || '#' } data-eid={ leftEid }>
            { leftIcon }
          </Link>
        </li>
        <li>
          <Link
            ariaLabel={ props.messages[golf.myAccount] }
            href={ (props.urls.account && props.urls.account.href) || '#' }
            data-eid={ eids.myAccount }>
            <span aria-hidden='true'>
              <User width={ 24 } height={ 24 } />
            </span>
          </Link>
        </li>
        <li>
          <Link
            ariaLabel={ props.messages[golf.help] }
            href={ (props.urls.help && props.urls.help.href) || '#' }
            data-eid={ eids.help }>
            <span className='icon-help' aria-hidden='true'>
              <Help width={ 24 } height={ 24 } />
            </span>
          </Link>
        </li>
      </ul>
    );
  }

  /**
   * Render translucent background behind the navigation menu
   *
   * @private
   *
   * @returns {ReactElement} div for the background
   */
  renderBackground() {
    if (!this.state.open) {
      return null;
    }
    return <div onClick={ this.close } className='mobile-bkg' />;
  }

  /**
   * Render main button with aria-label
   *
   * @param {String} ariaLabel Text for aria-label
   * @private
   * @returns {ReactElement} a main button
   */
  getButton(ariaLabel) {
    const { props } = this;

    let buttonTitle;
    if (props.useActiveHeadingAsMenuTitle && props.activeHeading) {
      if (props.activeHeading.caption) {
        buttonTitle = props.activeHeading.caption;
      } else if (props.activeHeading.title) {
        buttonTitle = props.activeHeading.title;
      }
    }
    if (!buttonTitle) {
      buttonTitle = props.menuTitle;
    }

    const trayName = (
      <span className='mobile-tray-name'>
        { props.showTitle && buttonTitle }
      </span>
    );

    return (
      <button
        type='button'
        onClick={ this.toggleOpen }
        className='nav-container btn'
        aria-label={ ariaLabel }
        data-eid={ props.menuButtonEid }>
        { props.privateLabelId !== 1 && (
          <span className='hamburger-icon'>
            <Hamburger width={ 24 } height={ 24 } />
          </span>
        ) }
        { trayName }
      </button>
    );
  }

  render() {
    const { props, state } = this;

    return (
      <IntlProvider locale={ props.market } messages={ props.messages }>
        <div
          className={ classnames(props.className, 'mobile-top-menu-item') }>
          {typeof props.menuTitleAriaLabel !== 'undefined' &&
            this.getButton(props.menuTitleAriaLabel)}
          {typeof props.menuTitleAriaLabel === 'undefined' && (
            <FormattedMessage id={ golf.mainMenu }>
              { text => this.getButton(text) }
            </FormattedMessage>
          )}
          { this.renderBackground() }
          <div
            className={ classnames('mobile-navigation', {
              open: state.open
            }) }
            onScroll={ this.killRunway }>
            { this.renderHeadingsPanel() }
            { props.showButtons && this.renderButtons() }
            { this.renderSubNavPanels() }
            <div
              className={ classnames('runway-container', {
                show: state.runway
              }) }>
              <span className='icon-chev-down-lt'>
                <ChevDown width={ 24 } height={ 24 } />
              </span>
              <span className='icon-chev-down-lt'>
                <ChevDown width={ 24 } height={ 24 } />
              </span>
              <span className='icon-chev-down-lt'>
                <ChevDown width={ 24 } height={ 24 } />
              </span>
            </div>
          </div>
        </div>
      </IntlProvider>
    );
  }
}
MobileNav.propTypes = {
  activeHeading: PropTypes.object,
  className: PropTypes.string,
  currentHref: PropTypes.string.isRequired,
  eidPrefix: PropTypes.string,
  growthnav2: PropTypes.bool,
  isInGoCohort: PropTypes.bool,
  market: PropTypes.string.isRequired,
  menuTitleAriaLabel: PropTypes.string,
  messages: PropTypes.object.isRequired,
  nav: PropTypes.arrayOf(PropTypes.object).isRequired,
  menuButtonEid: PropTypes.string,

  menuTitle: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.instanceOf(FormattedMessage)
  ]),

  onClick: PropTypes.func,
  privateLabelId: PropTypes.number,
  showButtons: PropTypes.bool,
  showHomeLink: PropTypes.bool,
  showMenuTitleOnHome: PropTypes.bool,
  showTitle: PropTypes.bool,
  urlArgs: PropTypes.object,
  urls: PropTypes.object.isRequired,
  useActiveHeadingAsMenuTitle: PropTypes.bool,
  useGradientHighlightBar: PropTypes.bool
};

/**
 * Default property values.
 *
 * @type {Object}
 * @public
 */
MobileNav.defaultProps = {
  currentHref: '',
  eidPrefix: 'uxp.hyd.mobile_nav',
  growthnav2: false,
  menuTitle: <FormattedMessage id='Shared:MobileNav:MainMenu' />,
  market: 'en-US',
  onClick: () => {},
  showButtons: true,
  showHomeLink: false,
  showMenuTitleOnHome: false,
  showTitle: true,
  useActiveHeadingAsMenuTitle: false,
  useGradientHighlightBar: false
};

export default MobileNav;
